import {
  Box,
  Button,
  Cascader,
  Flex,
  SelectField,
  type SelectFieldProps,
  Text,
} from '@/components';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOrganizationOptionsRecursive } from '../../organization/_hooks/organization';
import { useUserOptionsForSelectField } from '../_hooks/user';

type Props<T extends FieldValues> = {
  filterIds?: string[];
  organizationIds?: string[];
  organizationFilter?: 'modal' | 'settingField';
  useNotInOption?: boolean;
  useExistenceCheckOptions?: boolean;
  snapshotId?: string;
} & Omit<SelectFieldProps<T>, 'options' | 'showSearch'>;

export const UserSelectField = <T extends FieldValues>({
  filterIds,
  organizationIds: _organizationIds,
  organizationFilter,
  useNotInOption,
  useExistenceCheckOptions = true,
  snapshotId,
  ...props
}: Props<T>) => {
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState<string[]>([]);
  const [active, setActive] = useState(false);

  const filterLastId = filterValue.at(-1);
  const organizationIds =
    _organizationIds ?? (active && filterLastId ? [filterLastId] : undefined);

  const allUserOptions = useUserOptionsForSelectField({
    params: { snapshotId },
  });
  const organizationUserOptions = useUserOptionsForSelectField({
    params: { snapshotId },
    options: {
      organizationIds,
      pickUpUserIds: filterIds,
    },
  });

  return (
    <SelectField
      {...props}
      options={organizationUserOptions.data}
      existenceCheckOptions={
        useExistenceCheckOptions
          ? allUserOptions.data
          : organizationUserOptions.data
      }
      loading={allUserOptions.isPending || organizationUserOptions.isPending}
      showSearch
      useNotInOption={useNotInOption || !!_organizationIds?.length}
      textNotInOption={t('not-exist-in-user-options')}
      {...(!_organizationIds?.length &&
        organizationFilter &&
        (organizationFilter === 'modal'
          ? {
              filterOptionModal: {
                active: filterValue.length > 0,
                modalTitle: t('filter-by-organization'),
                component: (
                  <OrganizationFilterModal
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    snapshotId={snapshotId}
                  />
                ),
                onConfirm: () => {
                  setActive(true);
                },
                onClear: () => {
                  setActive(false);
                  setFilterValue([]);
                },
              },
            }
          : {
              filterOptionSettingField: {
                key: 'UserSelectFieldOrganizationFilter',
                active,
                component: (
                  <OrganizationFilterSettingField
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    setActive={setActive}
                    snapshotId={snapshotId}
                  />
                ),
              },
            }))}
    />
  );
};

function OrganizationFilterModal({
  filterValue,
  setFilterValue,
  snapshotId,
}: {
  filterValue: string[];
  setFilterValue: (value: string[]) => void;
  snapshotId?: string;
}) {
  const organizationOptions = useOrganizationOptionsRecursive({
    params: { snapshotId },
  });

  return (
    <Box paddingBlock={16}>
      <Cascader
        value={filterValue}
        onChange={(value) => {
          setFilterValue(value ? value.map((v) => String(v)) : []);
        }}
        options={organizationOptions.data ?? []}
        style={{ width: '100%' }}
        changeOnSelect
        allowClear
        loading={organizationOptions.isPending}
      />
    </Box>
  );
}

function OrganizationFilterSettingField({
  filterValue,
  setFilterValue,
  setActive,
  snapshotId,
}: {
  filterValue: string[];
  setFilterValue: (value: string[]) => void;
  setActive: (value: boolean) => void;
  snapshotId?: string;
}) {
  const { t } = useTranslation();

  const organizationOptions = useOrganizationOptionsRecursive({
    params: { snapshotId },
  });
  return (
    <Box padding={8}>
      <Flex vertical gap={8}>
        <Text weight="regular" text={t('organization')} color="secondary" />
        <Flex gap={10}>
          <Cascader
            value={filterValue}
            onChange={(value) => {
              if (!value) {
                setFilterValue([]);
                setActive(false);
                return;
              }

              setFilterValue(value ? value.map((v) => String(v)) : []);
            }}
            options={organizationOptions.data ?? []}
            style={{ width: '100%' }}
            changeOnSelect
            allowClear
            loading={organizationOptions.isPending}
          />
          <Button
            onClick={() => {
              setActive(true);
            }}
          >
            {t('apply-filter')}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
